/**
 * Catches a crash in any component below it and shows what happened.
 *
 * Without this, one thrown error unmounts the whole tree and the page goes
 * blank — no message on screen, and the only clue is in the browser console.
 * That is very hard to debug, so the error is put on the page instead.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";

type Props = { children: ReactNode };
type State = { error: Error | null; componentStack: string | null };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ componentStack: info.componentStack ?? null });
    console.error("Unhandled error in React tree:", error, info.componentStack);
  }

  render() {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="min-h-screen bg-white p-8 font-sans">
        <div className="mx-auto max-w-3xl">
          <h1 className="text-lg font-semibold text-red-700">Something crashed</h1>
          <p className="mt-1 text-sm text-slate-600">
            The screen below failed to render. The message and stack are shown so the cause
            is visible rather than hidden in the console.
          </p>

          <pre className="mt-4 overflow-x-auto rounded-lg bg-red-50 p-4 text-xs leading-relaxed text-red-900">
            {error.name}: {error.message}
          </pre>

          {error.stack && (
            <pre className="mt-3 max-h-64 overflow-auto rounded-lg bg-slate-50 p-4 text-[11px] leading-relaxed text-slate-700">
              {error.stack}
            </pre>
          )}

          {componentStack && (
            <pre className="mt-3 max-h-64 overflow-auto rounded-lg bg-slate-50 p-4 text-[11px] leading-relaxed text-slate-700">
              {componentStack}
            </pre>
          )}

          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-4 rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
